import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useAtomValue } from 'jotai';

import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { Button } from '@archipro/rocco/components/button';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { Input } from '@rocco/components/input';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { PopoverAnchor } from '@rocco/components/popover';
import { RoccoLink } from '@rocco/components/link/RoccoLink';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Textarea } from '@rocco/components/textarea';
import { usePlaceFinder } from '@modules/location/hook/use-place-finder';
import { getClosestBasicPlace } from '@modules/location/util';
import { useUserLoggedIn } from '@modules/user';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import type { Branch } from '@modules/enquiry/util/extract-branch-from-network.server';
import {
    useEnquiry,
    type UseEnquiryProps,
} from '@modules-rocco/enquiry/hooks/use-enquiry';
import { PostcodePopover } from './PostcodePopover';
import { FileAttachment } from './FileAttachment';
import { BuildHubSelector } from './components/BuildHubSelector';
import { fileStorage } from '~/utils/file-storage'; // your IndexedDB wrapper
import { useABTestIsOn } from '~/modules/root/hook/use-growthbook';
import { useFeature } from '~/modules/root';
import { useGetBuildHubsList } from '@ui-rocco/hooks/use-build-hubs';
import { EnquiryPreview } from './components/EnquiryPreview';
import { useBuildHubFieldSelection } from './hooks/use-build-hub-field-selection';

export interface EnquiryFormProps {
    className?: string;
    /** The professional to submit an enquiry to. */
    professional: Pick<
        ProfessionalInitialResult,
        'ID' | 'Title' | 'branchOptions' | 'CustomEnquiryMessage'
    >;
    /** The type of the Library Item (professional, product, project, article). */
    itemType: UseEnquiryProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: UseEnquiryProps['siteTreeID'];
    /** The source of the enquiry. */
    source: UseEnquiryProps['source'];
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /**
     * Include a branch dropdown if the professional has branches. Defaults to `false`.
     */
    includeBranch?: boolean;
    /** The enquiry form is in a modal. Defaults to `false`. */
    isModal?: boolean;
    /**
     * Show the professional's custom enquiry message from their settings. Defaults to `true`.
     */
    showCustomEnquiryMessage?: boolean;
    /** Override the default submit button text. Defaults to `"Submit"`. */
    submitButtonText?: string;
    /** Disable the submit button. Defaults to `false`. */
    disableSubmit?: boolean;
    /** Play the success animation and show the success message. Defaults to `true`. */
    showSuccessMessage?: boolean;
    /** Disable auto-focus on form fields. Defaults to `false`. */
    disableAutoFocus?: boolean;
    /** Whether the mobile keyboard is visible. Used for mobile Safari keyboard handling. */
    isKeyboardVisible?: boolean;
    /** Called when the enquiry is successfully submitted. */
    onSuccess?: UseEnquiryProps['onSuccess'];
    /** Called when the enquiry fails to submit. */
    onFailure?: UseEnquiryProps['onFailure'];
    /** Called when the user cancels login. */
    onAuthCancel?: UseEnquiryProps['onAuthCancel'];
}

/**
 * Form for submitting an enquiry to a professional. This component handles
 * form validation, user submission, and displays the login/signup form if the
 * user submits but is not logged in.
 */
export const EnquiryForm = ({
    className,
    professional,
    itemType,
    siteTreeID,
    source,
    relatedItem,
    includeBranch = false,
    isModal = false,
    showCustomEnquiryMessage = true,
    submitButtonText = 'Submit',
    disableSubmit = false,
    showSuccessMessage = true,
    disableAutoFocus = false,
    isKeyboardVisible = false,
    onSuccess,
    onFailure,
    onAuthCancel,
}: EnquiryFormProps) => {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const isBuildHubEnabled = useFeature('buildhub');
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
    const [isSuccessAnimationPlaying, setIsSuccessAnimationPlaying] =
        useState(false);
    const [postcodeSearch, setPostcodeSearch] = useState('');
    const [isPostcodeSearchOpen, setIsPostcodeSearchOpen] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const postcodeInputRef = useRef<HTMLInputElement | null>(null);
    const successAnimationTimeout = useRef<number>();
    const formRef =
        useRef<ReturnType<typeof useForm<z.infer<typeof formSchema>>>>();

    const [buildHubs, isBuildHubsLoading, fetchBuildHubs] =
        useGetBuildHubsList();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    useEffect(() => {
        fetchBuildHubs();
    }, [fetchBuildHubs]);

    // Add a session ID for file storage
    const sessionId = useMemo(
        () => `${professional.ID}-${siteTreeID || ''}`,
        [professional.ID, siteTreeID]
    );

    const playSuccessAnimation = useCallback(() => {
        clearTimeout(successAnimationTimeout.current);
        setIsSubmitSuccess(true);
        setIsSuccessAnimationPlaying(true);
        successAnimationTimeout.current = setTimeout(
            () => {
                setIsSuccessAnimationPlaying(false);
            },
            3000,
            undefined
        );
    }, []);

    const {
        defaultFormValues,
        formSchema,
        state,
        autoSubmitDelay,
        submitEnquiry,
        saveEnquirySession,
    } = useEnquiry({
        professional,
        itemType,
        siteTreeID,
        source,
        relatedItem,
        includeBranch,
        isModal,
        onSuccess: () => {
            formRef.current?.reset();
            setFiles([]);
            fileStorage.clearFiles(sessionId).catch(console.error);
            playSuccessAnimation();
            onSuccess?.();
        },
        onFailure,
        onAuthCancel,
    });

    const isEnquiryAttachment = useABTestIsOn('enquiry-attachment');

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        disabled: state !== 'idle' || autoSubmitDelay,
        mode: 'onSubmit',
        defaultValues: defaultFormValues,
    });

    // Store form in ref so it can be accessed in callbacks
    formRef.current = form;

    // Get selected BuildHub ID for field selection (after form is initialized)
    const selectedBuildHubId = form.watch('buildHubId');
    const [fieldSelection, setFieldSelection] =
        useBuildHubFieldSelection(selectedBuildHubId);

    const {
        changeKeyword: findPlace,
        dropdownOptions: placeOptions,
        loading: isPlaceOptionsLoading,
    } = usePlaceFinder({
        limit: 10,
        debounceMs: 250,
    });

    const autoSelectBranch = useCallback(
        (postcode: (typeof placeOptions)[number]) => {
            if (
                !includeBranch ||
                !professional.branchOptions.length ||
                form.getValues('branchId')
            ) {
                return;
            }
            const branches = professional.branchOptions.map((p) => p.value);
            const closestPlace = getClosestBasicPlace(
                postcode.value,
                branches
            ) as Branch | undefined;
            const closestBranch = branches.find((branch) => {
                return branch.branchId === closestPlace?.branchId;
            });
            if (closestBranch) {
                form.setValue('branchId', closestBranch.branchId.toString());
            }
        },
        [includeBranch, professional, form]
    );

    const selectPostcode = useCallback(
        (place: (typeof placeOptions)[number] | undefined) => {
            if (place) {
                form.setValue('postcode', place);
                setPostcodeSearch(place.header);
                autoSelectBranch(place);
            } else {
                // @ts-expect-error - force postcode to be null.
                form.setValue('postcode', null);
                setPostcodeSearch('');
            }
            setIsPostcodeSearchOpen(false);
            saveEnquirySession({
                ...form.getValues(),
                postcode: place,
            });
        },
        [form, autoSelectBranch, saveEnquirySession]
    );

    /**
     * Automatically select the first postcode option if the user has typed a
     * postcode, there are options available, and postcode is not set yet.
     */
    const autoSelectPostcode = useCallback(() => {
        if (
            postcodeSearch &&
            placeOptions.length &&
            !form.getValues('postcode')
        ) {
            selectPostcode(placeOptions[0]);
            form.clearErrors('postcode');
        }
    }, [postcodeSearch, placeOptions, form, selectPostcode]);

    /**
     * Select the first available postcode option from the dropdown options.
     * Does not change the selection if the selected postcode is in the dropdown.
     */
    const selectAvailablePostCode = useCallback(() => {
        if (placeOptions.length && !isPlaceOptionsLoading) {
            const selectedPlace = placeOptions.find(
                (place) =>
                    place.value.addressID ===
                    form.getValues('postcode')?.value.addressID
            );
            if (selectedPlace) {
                selectPostcode(selectedPlace);
            } else {
                selectPostcode(placeOptions[0]);
            }
        }
    }, [placeOptions, isPlaceOptionsLoading, form, selectPostcode]);

    // Modify the file change handler to save to IndexedDB
    const handleFileChange = useCallback(
        async (newFiles: File[]) => {
            setFiles(newFiles);
            await fileStorage.saveFiles(sessionId, newFiles);
            saveEnquirySession(form.getValues(), newFiles);
        },
        [sessionId, form, saveEnquirySession]
    );

    useEffect(() => {
        // Auto select the first postcode option if the input is not focused and no value is set.
        if (document.activeElement !== postcodeInputRef.current) {
            autoSelectPostcode();
        }
    }, [autoSelectPostcode]);

    useEffect(() => {
        if (!form.formState.isDirty && !form.formState.isSubmitted) {
            // This is required to load the saved session data.
            form.reset(defaultFormValues);
            if (!postcodeSearch && form.getValues('postcode')) {
                setPostcodeSearch(form.getValues('postcode').header);
            }

            // Restore files from IndexedDB.
            fileStorage.getFiles(sessionId).then((restoredFiles) => {
                if (restoredFiles.length) {
                    setFiles(restoredFiles);
                }
            });
        }
    }, [defaultFormValues, form, postcodeSearch, sessionId]);

    if (isPreviewOpen) {
        const selectedBuildHub = buildHubs?.find(
            (b) => String(b.ID) === selectedBuildHubId
        );
        const message = form.watch('message');

        if (selectedBuildHub) {
            return (
                <div className={cn('relative h-full', className)}>
                    <EnquiryPreview
                        buildHub={selectedBuildHub}
                        message={message}
                        onBack={() => setIsPreviewOpen(false)}
                        fieldSelection={fieldSelection}
                    />
                </div>
            );
        }
    }

    return (
        <div className={cn('relative', className)}>
            <div
                className={cn(
                    'space-y-8',
                    isSubmitSuccess && showSuccessMessage && 'invisible'
                )}
            >
                {showCustomEnquiryMessage &&
                    !!professional.CustomEnquiryMessage && (
                        <div className="whitespace-pre-wrap text-base">
                            {professional.CustomEnquiryMessage}
                        </div>
                    )}
                <Form {...form}>
                    <form
                        id="enquiry-form"
                        onSubmit={form.handleSubmit((data) => {
                            if (state !== 'idle') {
                                return;
                            }
                            submitEnquiry(data, files, fieldSelection);
                        })}
                        className="grid grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-2"
                    >
                        <FormField
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="text-base"
                                        errorVariant="errorDarker"
                                    >
                                        Full Name*
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            text="default"
                                            color="lightOutline"
                                            placeholder="e.g. John Doe"
                                            autoComplete="name"
                                            // Don't autofocus in-page or it will hijack
                                            // the auto-submission enquiry session.
                                            // The last input with autoFocus={true} will be focused.
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !field.value
                                            }
                                            {...field}
                                            onBlur={() => {
                                                field.onBlur();
                                                saveEnquirySession(
                                                    form.getValues()
                                                );
                                            }}
                                            onFocus={(e) => {
                                                e.target.select?.();
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage variant="darker" />
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="email"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="text-base"
                                        errorVariant="errorDarker"
                                    >
                                        Email*
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="email"
                                            text="default"
                                            color="lightOutline"
                                            placeholder="e.g. johndoe@gmail.com"
                                            autoComplete="email"
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !isLoggedIn &&
                                                !!form.getValues('name') &&
                                                !field.value
                                            }
                                            disabled={
                                                isLoggedIn && !!user.Email
                                            }
                                            {...field}
                                            onBlur={() => {
                                                field.onBlur();
                                                saveEnquirySession(
                                                    form.getValues()
                                                );
                                            }}
                                            onFocus={(e) => {
                                                e.target.select?.();
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage variant="darker" />
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="phoneNumber"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="text-base"
                                        errorVariant="errorDarker"
                                    >
                                        Phone number*
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="tel"
                                            text="default"
                                            color="lightOutline"
                                            placeholder="+1 234 567 8910"
                                            autoComplete="tel"
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !!form.getValues('name') &&
                                                !!form.getValues('email') &&
                                                !field.value
                                            }
                                            {...field}
                                            onBlur={() => {
                                                field.onBlur();
                                                saveEnquirySession(
                                                    form.getValues()
                                                );
                                            }}
                                            onFocus={(e) => {
                                                e.target.select?.();
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage variant="darker" />
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="postcode"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="text-base"
                                        errorVariant="errorDarker"
                                    >
                                        Suburb / Postcode*
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            text="default"
                                            color="lightOutline"
                                            showClearButton
                                            autoComplete="off"
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !!form.getValues('name') &&
                                                !!form.getValues('email') &&
                                                !!form.getValues(
                                                    'phoneNumber'
                                                ) &&
                                                !field.value
                                            }
                                            maxLength={100}
                                            {...field}
                                            ref={(el) => {
                                                postcodeInputRef.current = el;
                                                field.ref(el);
                                            }}
                                            value={postcodeSearch}
                                            onChange={(e) => {
                                                setPostcodeSearch(
                                                    e.target.value
                                                );
                                                findPlace(e.target.value);
                                                if (e.target.value) {
                                                    setIsPostcodeSearchOpen(
                                                        true
                                                    );
                                                } else {
                                                    selectPostcode(undefined);
                                                }
                                            }}
                                            onFocus={(e) => {
                                                if (postcodeSearch) {
                                                    setIsPostcodeSearchOpen(
                                                        true
                                                    );
                                                    if (!placeOptions.length) {
                                                        findPlace(
                                                            postcodeSearch
                                                        );
                                                    }
                                                }
                                                e.target.select?.();
                                            }}
                                            onBlur={() => {
                                                field.onBlur();
                                                autoSelectPostcode();
                                                setIsPostcodeSearchOpen(false);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    selectAvailablePostCode();
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <PostcodePopover
                                        open={isPostcodeSearchOpen}
                                        onOpenChange={(open) => {
                                            if (
                                                !open &&
                                                document.activeElement ===
                                                    postcodeInputRef.current
                                            ) {
                                                // Prevent popover from closing when the input is focused.
                                                return;
                                            }
                                            setIsPostcodeSearchOpen(open);
                                        }}
                                        value={field.value}
                                        placeOptions={placeOptions}
                                        isLoading={isPlaceOptionsLoading}
                                        onSelect={selectPostcode}
                                    >
                                        <div className="absolute">
                                            <PopoverAnchor className="-mt-2" />
                                        </div>
                                    </PostcodePopover>
                                    <FormMessage variant="darker" />
                                </FormItem>
                            )}
                        />
                        {isBuildHubEnabled && isLoggedIn && (
                            <FormField
                                name="buildHubId"
                                control={form.control}
                                render={({ field }) => (
                                    <BuildHubSelector
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value);
                                            saveEnquirySession(
                                                form.getValues()
                                            );
                                        }}
                                        disabled={field.disabled}
                                        onBlur={field.onBlur}
                                        buildHubs={buildHubs}
                                        isLoading={isBuildHubsLoading}
                                        fieldSelection={fieldSelection}
                                        onFieldSelectionChange={
                                            setFieldSelection
                                        }
                                        onRefetchBuildHubs={fetchBuildHubs}
                                    />
                                )}
                            />
                        )}
                        {includeBranch &&
                            !!professional.branchOptions?.length && (
                                <FormField
                                    name="branchId"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem className="md:col-span-2">
                                            <FormLabel
                                                className="text-base"
                                                errorVariant="errorDarker"
                                            >
                                                Branch*
                                            </FormLabel>
                                            <Select
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    saveEnquirySession(
                                                        form.getValues()
                                                    );
                                                }}
                                                value={field.value}
                                                disabled={field.disabled}
                                            >
                                                <FormControl>
                                                    <SelectTrigger
                                                        className="w-full"
                                                        color="lightOutline"
                                                    >
                                                        <SelectValue placeholder="Select branch" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent
                                                    className="bg-transparent backdrop-blur-2xl"
                                                    borderless
                                                >
                                                    {professional.branchOptions.map(
                                                        (branch) => (
                                                            <SelectItem
                                                                key={
                                                                    branch.value
                                                                        .branchId
                                                                }
                                                                className="py-4 pr-4 focus:bg-black/10"
                                                                value={branch.value.branchId.toString()}
                                                            >
                                                                {branch.header}
                                                            </SelectItem>
                                                        )
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage variant="darker" />
                                        </FormItem>
                                    )}
                                />
                            )}
                        <FormField
                            name="message"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem className="md:col-span-2">
                                    <FormLabel
                                        className="text-base"
                                        errorVariant="errorDarker"
                                    >
                                        Message*
                                    </FormLabel>
                                    <FormControl>
                                        <Textarea
                                            text="default"
                                            color="lightOutline"
                                            placeholder="Please be descriptive with your enquiry to ensure a good response."
                                            autoComplete="off"
                                            rows={isDesktop ? 8 : 7}
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !!form.getValues('name') &&
                                                !!form.getValues('email') &&
                                                !!form.getValues(
                                                    'phoneNumber'
                                                ) &&
                                                !!form.getValues('postcode')
                                            }
                                            style={{
                                                // Prevent textarea from shrinking when keyboard is visible
                                                ...(isKeyboardVisible && {
                                                    minHeight: '120px',
                                                    resize: 'none',
                                                }),
                                            }}
                                            {...field}
                                            onFocus={(e) => {
                                                e.target.select?.();
                                            }}
                                            onBlur={() => {
                                                field.onBlur();
                                                saveEnquirySession(
                                                    form.getValues()
                                                );
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage variant="darker" />
                                </FormItem>
                            )}
                        />
                        {isEnquiryAttachment ? (
                            <div className="flex flex-row items-start md:col-span-2">
                                {isDesktop && (
                                    <div>
                                        <FileAttachment
                                            files={files}
                                            onChange={handleFileChange}
                                            maxAttachments={5}
                                            disabled={form.formState.disabled}
                                        />
                                    </div>
                                )}
                                <div className="flex-grow"></div>
                                <div>
                                    <Button
                                        type="submit"
                                        className="px-9 py-2.5"
                                        disabled={
                                            disableSubmit ||
                                            form.formState.disabled
                                        }
                                        isLoading={form.formState.disabled}
                                    >
                                        {form.formState.disabled
                                            ? 'Sending...'
                                            : submitButtonText}
                                        {!form.formState.disabled && (
                                            <ArrowRight className="ml-2 size-5" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-4 flex justify-end md:col-span-2">
                                <Button
                                    type="submit"
                                    className="px-9 py-2.5"
                                    disabled={
                                        disableSubmit || form.formState.disabled
                                    }
                                    isLoading={form.formState.disabled}
                                >
                                    {submitButtonText}
                                    <ArrowRight className="ml-2 size-5" />
                                </Button>
                            </div>
                        )}
                    </form>
                </Form>
            </div>
            {showSuccessMessage && (
                <div
                    className={cn(
                        'absolute left-0 top-0 z-1 hidden h-full w-full flex-col overflow-hidden rounded-xl',
                        isSubmitSuccess && 'flex'
                    )}
                    role="alert"
                    aria-live="assertive"
                >
                    <img
                        src="/assets/website/ui/images/glassy-background.png"
                        alt=""
                        className="pointer-events-none absolute left-0 top-0 -z-1 h-full w-full select-none object-cover"
                    />
                    <div className="border-b border-white px-4 pb-4 pt-6 text-lg md:px-8">
                        Message sent
                    </div>
                    <div className="relative flex grow flex-col items-center justify-center p-4 text-center text-base will-change-contents md:p-8">
                        {/* Need to recreate video to start playback from beginning. */}
                        {isSubmitSuccess && (
                            <video
                                src="/assets/website/ui/videos/message-sent.mp4"
                                autoPlay
                                playsInline
                                muted
                                className={cn(
                                    'absolute left-1/2 top-1/2 size-20 -translate-x-1/2 -translate-y-1/2 transform-gpu mix-blend-darken duration-300 will-change-transform',
                                    !isSuccessAnimationPlaying &&
                                        'translate-y-[calc(-50%-3.5rem)] transition-transform'
                                )}
                            />
                        )}
                        {!isSuccessAnimationPlaying && (
                            <div className="duration-500 will-change-[opacity] animate-in fade-in">
                                <div className="mb-10 h-20">
                                    {/* Imaginary icon */}
                                </div>
                                <div className="mb-2 text-lg font-medium">
                                    Thank you for your enquiry!
                                </div>
                                <div>
                                    We will get back to you as soon as possible.
                                </div>
                                <div>
                                    You will receive a notification in your
                                    email:{' '}
                                    {isLoggedIn
                                        ? user.Email
                                        : form.getValues('email')}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-x-4 border-t border-white px-4 py-4 md:gap-x-6 md:px-8">
                        <Button
                            color="white"
                            weight="normal"
                            className="px-6 py-2.5 md:px-8"
                            asChild
                        >
                            <RoccoLink to="/member/inbox" isNonRemixRoute>
                                View My Inbox
                                <ArrowRight className="ml-2 size-5" />
                            </RoccoLink>
                        </Button>
                        <Button
                            className="px-8 py-2.5 md:px-16"
                            onClick={() => setIsSubmitSuccess(false)}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
